import throttle from 'lodash/throttle';
import {useEffect, useState, useRef} from "react";

// originally based on
// https://github.com/NotionX/react-notion-x/blob/master/packages/react-notion-x/src/block.tsx#L128-L161

export default function useScrollspy() {
  const [activeSection, setActiveSection] = useState(null);
  const [isClient, setIsClient] = useState(false);
  const throttleMs = 100;
  const isProgrammaticScroll = useRef(false);
  const scrollTimeout = useRef(null);

  const actionSectionScrollSpy = throttle(() => {
    // Skip if this is a programmatic scroll
    if (isProgrammaticScroll.current) return;

    const sections = document.getElementsByClassName('hash-anchor');

    let prevBBox = null;
    let currentSectionId = activeSection;

    for (let i = 0; i < sections.length; ++i) {
      const section = sections[i];

      if (!currentSectionId) {
        currentSectionId = section.getAttribute('href')?.split('#')[1] ?? null;
      }

      const bbox = section.getBoundingClientRect();
      const prevHeight = prevBBox ? bbox.top - prevBBox.bottom : 0;
      const offset = Math.max(200, prevHeight / 4);

      // GetBoundingClientRect returns values relative to viewport
      if (bbox.top - offset < 0) {
        currentSectionId = section.getAttribute('href')?.split('#')[1] ?? null;

        prevBBox = bbox;
        continue;
      }

      // No need to continue loop, if last element has been detected
      break;
    }

    setActiveSection(currentSectionId);
  }, throttleMs);

  useEffect(() => {
    setIsClient(true)
  }, [])

  // Function to handle programmatic scrolls (like TOC clicks)
  const handleProgrammaticScroll = (targetId) => {
    isProgrammaticScroll.current = true;
    
    // Clear any existing timeout
    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }
    
    // Set a timeout to re-enable scroll detection after scroll animation completes
    scrollTimeout.current = setTimeout(() => {
      isProgrammaticScroll.current = false;
    }, 1000); // 1 second should be enough for most scroll animations
    
    // Update active section immediately for the target
    setActiveSection(targetId);
  };

  useEffect(() => {
    if (!isClient) return;
    
    window.addEventListener('scroll', actionSectionScrollSpy);

    actionSectionScrollSpy();

    return () => {
      window.removeEventListener('scroll', actionSectionScrollSpy);
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClient]);

  return { activeSection, handleProgrammaticScroll };
}
